import { ClientStorage } from '@medplum/core';

/**
 * Clears local Medplum auth storage when the stored session is stale or mismatched.
 *
 * Two cases are handled before constructing MedplumClient (so the SDK doesn't try to use bad state):
 *
 * 1. Server returns 404/410 for GET /auth/me — resource deleted (e.g. by rbac-pilot:reset).
 * 2. Stored login `clientId` doesn't match the configured clientId — happens when the app is
 *    reconfigured with a new OAuth client (e.g. switching from setup-medplum.sh client to
 *    the seeded pilot client). The old refresh token is bound to the old client_id and will
 *    cause Medplum to return "Invalid client" on the token refresh attempt.
 */
export async function clearStaleMedplumSessionIfNeeded(
  baseUrl: string,
  configuredClientId?: string,
): Promise<void> {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  const storage = new ClientStorage(window.localStorage, '');
  const active = storage.getObject<{ accessToken?: string }>('activeLogin');
  if (!active?.accessToken) {
    return;
  }

  // The Medplum SDK stores clientId as a separate key, not inside activeLogin.
  // Check that key so a stale session from a deleted/rotated ClientApplication is cleared.
  const storedClientId = storage.getString('clientId');
  if (configuredClientId && storedClientId && storedClientId !== configuredClientId) {
    storage.clear();
    try {
      sessionStorage.clear();
    } catch {
      /* ignore */
    }
    return;
  }

  const url = `${baseUrl.replace(/\/$/, '')}/auth/me`;

  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/fhir+json',
        Authorization: `Bearer ${active.accessToken}`,
      },
      credentials: 'include',
    });

    if (res.status === 410 || res.status === 404) {
      storage.clear();
      try {
        sessionStorage.clear();
      } catch {
        /* ignore */
      }
    }
  } catch {
    /* offline or CORS — keep session */
  }
}
